import clsx from 'clsx'
import first from 'lodash/first'
import isEmpty from 'lodash/isEmpty'
import last from 'lodash/last'
import map from 'lodash/map'
import React, { FC, ReactNode, useEffect, useMemo, useRef, useState } from 'react'

import SvgAdd from '@Assets/SVG/Add'
import SvgCheck from '@Assets/SVG/Check'
import SvgClose from '@Assets/SVG/Close'
import SvgPlus from '@Assets/SVG/Plus'
import { BuyButtonV2 } from '@Components/BuyButton/v2'
import { ImageV4 } from '@Components/Image'
import { Slider, useSlider } from '@Components/Slider'
import { Wordbook } from '@Components/Wordbook'
import { Milestones } from '@CoreComponents/Progressbars/Milestones'
import { CartV2LineItems, CartV2Type } from '@Domains/Cart/Feature/CartV2/CartV2'
import { useDiscountCodes } from '@Domains/Cart/Feature/DiscountCodes'
import { ProductItem } from '@Domains/Product/Components/ProductItem'
import { CartMilestoneStepStatus, MilestoneFragmentFragment, Product } from '@Generated/graphql'
import { useSplitTest } from '@Globals/SplitTests/Hooks'
import { useTranslation } from '@Globals/Translations'
import { Spinner } from '@Shared/Components/Spinner/Spinner'

import styles from './CartMilestonesV2.module.scss'

type CartMileStones = NonNullable<MilestoneFragmentFragment['milestones']>
type CartMileStone = NonNullable<CartMileStones['steps']>[0]

interface CartMilestonesProps {
  cart?: CartV2Type | null
  className?: string
  inverted?: boolean
}
const CartMilestoneObtainableObject = ({ mileStone }: {
  mileStone: CartMileStone
}) => {
  const isComplete = mileStone.status === CartMilestoneStepStatus.Complete

  if (mileStone?.option?.__typename === 'CartMilestoneStepOptionProducts' && first(mileStone.option.products)) {
    const product: Product | undefined = first(mileStone.option.products)!

    return (
      <div className={clsx(styles.CartMilestoneObtainableObject, isComplete && styles.isComplete)}>
        <p className={styles.header}>
          {product.masterVariant!.price?.format}
        </p>
        <div className={styles.item}>
          {product.asset && <ImageV4 height={31} src={product.asset.url} width={31} />}
          <p>{product.type}</p>
        </div>
      </div>
    )
  }
  if (mileStone?.type === 'BUY_X_FOR_Y') {
    return (
      <div className={clsx(styles.CartMilestoneObtainableObject, isComplete && styles.isComplete)}>
        <p className={styles.header}>
          <Wordbook
            components={{ Link: <div /> }}
            wordbook={mileStone.title}
          />
        </p>
      </div>
    )
  }
  return null
}
const CartMilestoneStepOptionDiscountCode = ({ discountCodes,
  mileStone,
  onClose }) => {
  const { t } = useTranslation()
  const { apply } = useDiscountCodes()
  const [loading, setLoading] = useState(false)
  const disabled = mileStone?.status && ![CartMilestoneStepStatus.Unlocked, CartMilestoneStepStatus.Complete].includes(mileStone?.status)
  const appliedCodes = useMemo(() => discountCodes.map(d => d.code), [discountCodes])
  const currentCodeApplied = useMemo(() => appliedCodes.includes(mileStone.option.discountCode), [appliedCodes, mileStone.option.discountCode])
  if (!mileStone?.option?.__typename || mileStone?.option?.__typename !== 'CartMilestoneStepOptionDiscountCode') {
    return null
  }

  return (
    <div className={styles.cartMilestoneStepOptionDiscountCode}>
      <div className={styles.selectHeader}>
        {mileStone.option?.title && (
          <Wordbook components={{ Link: <div /> }} wordbook={mileStone.option?.title} />
        )}
        <button onClick={onClose} style={{ zIndex: 1 }} type="button">
          <span>{t('common:CLOSE')}</span>
          <SvgClose />
        </button>
      </div>
      <div className={clsx(styles.claimWrapper, disabled && styles.unavailable)}>
        <p>
          {mileStone.option.discountCode}
        </p>
        <button
          className={clsx(styles.claim, currentCodeApplied && styles.active)}
          onClick={() => {
            if (disabled) {
              return
            }
            setLoading(true)
            apply(mileStone.option!.discountCode)
              .then(() => setLoading(false))
          }}
          type="button"
        >
          {!loading && currentCodeApplied && <SvgCheck />}
          {loading && <Spinner className={styles.spinner} />}
          {!loading && !currentCodeApplied && (
            <Wordbook components={{ Link: <div /> }} wordbook={mileStone.option.callToAction} />
          )}
        </button>
      </div>
      {(!currentCodeApplied || disabled) && (
        <p className={styles.warning}>
          <Wordbook wordbook={mileStone.option.warning} />
        </p>
      )}
      {currentCodeApplied && !disabled && (
        <p className={styles.warning}>
          <Wordbook wordbook={mileStone.option.success} />
        </p>
      )}

    </div>
  )
}

const AutoClaimedProduct: FC<{
  lineItems: CartV2LineItems
  mileStone: CartMileStone
  onClose: () => void
}> = ({
  lineItems,
  mileStone,
  onClose,
}) => {
  const { t } = useTranslation()

  const disabled = mileStone?.status === CartMilestoneStepStatus.Progress

  const claimProductId = useMemo(
    () => lineItems.find(lineItem => lineItem.type?.milestoneID === mileStone.id)?.product?.id,
    [lineItems, mileStone.id],
  )
  if (!mileStone?.option?.__typename || mileStone?.option?.__typename !== 'CartMilestoneStepOptionProducts') {
    return null
  }

  const product = first(mileStone.option?.products)
  if (!product) {
    return null
  }
  return (
    <div className={clsx(styles.cartMilestoneStepOptionFreeBee, 'fullwidth')}>
      <div className={styles.selectHeader}>
        {mileStone.option?.title && (
          <Wordbook components={{ Link: <div /> }} wordbook={mileStone.option?.title} />
        )}
        <button onClick={onClose} style={{ zIndex: 1 }} type="button">
          <span>{t('common:CLOSE')}</span>
          <SvgClose />
        </button>
      </div>
      <ProductItem
        action={(
          <BuyButtonV2
            className={clsx(styles.AddToCartProduct, claimProductId === product.id && styles.success)}
            color="black"
            disabled={disabled || claimProductId === product.id}
            milestoneID={mileStone.id}
            onSuccess={onClose}
            productID={product.id}
            useCache={false}
            withoutSuccessPrompt
          >
            {(!claimProductId || claimProductId !== product.id) && (
              <SvgPlus />
            )}
            {claimProductId === product.id && <SvgCheck />}
          </BuyButtonV2>
          )}
        className={styles.productItem}
        content={product.name}
        disabled={disabled}
        discount={t('header:FREE_LABEL')}
        imageUrl={product.asset?.url || ''}
        price={product.masterVariant?.price?.format || ''}
      />
    </div>
  )
}
const CartMilestoneStepOptionProducts: FC<{
  lineItems: CartV2LineItems
  mileStone: CartMileStone
  onClose: () => void
}> = ({
  lineItems,
  mileStone,
  onClose,
}) => {
  const { t } = useTranslation()
  const { onSlide, sliderRef, sliderState } = useSlider()
  const claimProductId = useMemo(
    () => lineItems.find(lineItem => lineItem.type?.milestoneID === mileStone.id)?.product?.id,
    [lineItems, mileStone.id],
  )

  const disabled = mileStone?.status && ![CartMilestoneStepStatus.Unlocked, CartMilestoneStepStatus.Complete].includes(mileStone?.status)
  if (!mileStone?.option?.__typename || mileStone?.option?.__typename !== 'CartMilestoneStepOptionProducts') {
    return null
  }

  return (
    <div className={styles.cartMilestoneStepOptionProducts}>
      <div className={styles.selectHeader}>
        {mileStone.option?.title && (
          <Wordbook components={{ Link: <div /> }} wordbook={mileStone.option?.title} />
        )}
        <button onClick={onClose} style={{ zIndex: 1 }} type="button">
          <span>{t('common:CLOSE')}</span>
          <SvgClose />
        </button>
      </div>
      <Slider className={styles.slider} onSlide={onSlide} ref={sliderRef}>
        {map(mileStone.option.products, product => (
          <div
            key={`CartMilestoneStepOptionProduct_${product.id}`}
            className={clsx(styles.product, disabled && styles.disabled)}
          >
            {product.asset?.url && <ImageV4 className={styles.productImg} draggable={false} src={product.asset?.url} width={170} />}
            <span className={styles.price}>
              <p>{t('header:FREE_LABEL')}</p>
              <p>{product.masterVariant?.price?.format}</p>
            </span>
            <BuyButtonV2
              className={clsx(styles.AddToCartProduct, claimProductId === product.id && styles.success)}
              color="black"
              disabled={disabled || claimProductId === product.id}
              milestoneID={mileStone.id}
              onSuccess={onClose}
              productID={product.id}
              sourceID="choose_gift"
              useCache={false}
              withoutSuccessPrompt
            >
              {(!claimProductId || claimProductId !== product.id) && (
                <SvgPlus />
              )}
              {claimProductId === product.id && <SvgCheck />}
            </BuyButtonV2>
          </div>
        ))}
      </Slider>
      <input
        className={clsx(styles.range, !sliderState && styles.hidden)}
        max={sliderState?.rangeMax ?? 0}
        min={sliderState?.rangeMin ?? 0}
        onChange={() => null}
        type="range"
        value={sliderState?.rangeValue ?? 0}
      />
    </div>
  )
}

const MileStoneTypeIcons: Record<string, ReactNode> = {
  FREE_SHIPPING__COMPLETE: <SvgCheck />,
  FREE_SHIPPING__UNLOCKED: <SvgCheck />,
  FREE_SHIPPING__PROGRESS: <SvgCheck />,
  FREE_SHIPPING__UNAVAILABLE: <SvgCheck />,
  CHOOSE_GIFT__COMPLETE: <SvgCheck />,
  CHOOSE_GIFT__UNLOCKED: <SvgAdd />,
  CHOOSE_GIFT__PROGRESS: <SvgCheck />,
  CHOOSE_GIFT__UNAVAILABLE: <SvgCheck />,
  RELATIVE_DISCOUNT__COMPLETE: <SvgCheck />,
  RELATIVE_DISCOUNT__UNLOCKED: <SvgAdd />,
  RELATIVE_DISCOUNT__PROGRESS: <SvgCheck />,
  RELATIVE_DISCOUNT__UNAVAILABLE: <SvgCheck />,
  BUY_X_FOR_Y__PROGRESS: <SvgCheck />,
  BUY_X_FOR_Y__UNAVAILABLE: <SvgCheck />,
  BUY_X_FOR_Y__UNLOCKED: <SvgAdd />,
  BUY_X_FOR_Y__COMPLETE: <SvgCheck />,
}
export const CartMilestonesV2: FC<CartMilestonesProps> = ({ cart, className, inverted }) => {
  const milestones = cart?.milestones || undefined
  const ref = useRef<HTMLDivElement | null>(null)
  const [selectedMileStone, setSelectedMileStone] = useState<CartMileStone | null>(null)
  const useOneRowLook = useMemo(() => milestones?.steps?.some(step => step.type === 'EMPTY'), [milestones?.steps])
  const cartV3 = useSplitTest('cart_v3', 'b')

  useEffect(() => {
    const firstUnlocked = milestones?.steps?.find(step => step.status === CartMilestoneStepStatus.Unlocked)
    if (firstUnlocked) {
      setSelectedMileStone(firstUnlocked)
    } else {
      setSelectedMileStone(null)
    }
  }, [milestones?.steps])

  if (!milestones || isEmpty(milestones?.steps)) {
    return null
  }

  return (
    <div className={clsx(styles.cartMilestones, cartV3 && styles.cartMilestonesV3, className)} ref={ref}>
      <div className={styles.header}>
        <Wordbook components={{ Link: <div /> }} wordbook={milestones.title} />
      </div>
      <div className={clsx(styles.milestonesWrapper, useOneRowLook && styles.oneRowLook)}>
        <Milestones
          hideMilestoneIndicators
          inverted={inverted}
          milestones={milestones?.steps?.map((milestone, index) => {
            const isUnlocked = milestone.option && milestone.status === CartMilestoneStepStatus.Unlocked
            const isComplete = milestone.option && milestone.status === CartMilestoneStepStatus.Complete
            return ({
              id: milestone.id,
              icon: (useOneRowLook ? index + 1 : MileStoneTypeIcons[`${milestone.type}__${milestone.status}`]) || undefined,
              disabled: milestone.type === 'FREE_SHIPPING',
              onClick: () => {
                if (selectedMileStone === milestone) {
                  setSelectedMileStone(null)
                } else {
                  setSelectedMileStone(milestone)
                }
                ref.current?.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'start' })
              },
              buttonClassName: (isUnlocked && !isComplete && styles.milestoneBlackButton) || undefined,
              text:
                  useOneRowLook || (cartV3 && milestone.type === 'FREE_SHIPPING')
                    ? undefined
                    // eslint-disable-next-line react/jsx-no-useless-fragment
                    : <Wordbook components={{ Link: <></> }} wordbook={milestone.title} />,
              progress: milestone.progress || 0,
            })
          }) || []}
        />
        {useOneRowLook && (
          <CartMilestoneObtainableObject mileStone={last(milestones?.steps) as CartMileStone} />
        )}
      </div>

      {selectedMileStone?.option?.__typename === 'CartMilestoneStepOptionProducts'
          && (selectedMileStone.option.products || [])?.length > 1 && (
            <CartMilestoneStepOptionProducts lineItems={cart?.lineItems || []} mileStone={selectedMileStone} onClose={() => setSelectedMileStone(null)} />
      )}
      {selectedMileStone?.option?.__typename === 'CartMilestoneStepOptionProducts'
          && (selectedMileStone.option.products || [])?.length === 1 && (
            <AutoClaimedProduct
              lineItems={cart?.lineItems || []}
              mileStone={selectedMileStone}
              onClose={() => setSelectedMileStone(null)}
            />
      )}
      {selectedMileStone?.option?.__typename === 'CartMilestoneStepOptionDiscountCode' && (
        <CartMilestoneStepOptionDiscountCode
          discountCodes={cart?.discountCodes}
          mileStone={selectedMileStone}
          onClose={() => setSelectedMileStone(null)}
        />
      )}
    </div>
  )
}

